// Pure helpers for drawing members travelling together (`travelGroups`) on the
// live map and in its sidebar: one marker, one trail and one row per group.
import type { HistoryPoint } from "../history/history";
import type { MemberRow } from "./liveCircle";
import type { TravelGroup } from "./together";

const GROUP_PREFIX = "group:";

/** The map and sidebar key of a group (its marker, focus and row); user ids never take this form. */
export function groupKey(groupId: string): string {
  return GROUP_PREFIX + groupId;
}

/** The group id in a {@link groupKey}, or null for a member's user id. */
export function groupIdOf(key: string): string | null {
  return key.startsWith(GROUP_PREFIX) ? key.slice(GROUP_PREFIX.length) : null;
}

/** Avatars a group marker stacks before "+N". */
export const STACKED_AVATARS = 3;

/** The rows a group marker stacks (the freshest member first) and how many more it has. */
export function stackedRows(group: TravelGroup, rows: readonly MemberRow[]): { shown: MemberRow[]; more: number } {
  const members = groupRows(group, rows);
  const freshest = members.filter((row) => row.userId === group.freshestId);
  const ordered = [...freshest, ...members.filter((row) => row.userId !== group.freshestId)];
  return { shown: ordered.slice(0, STACKED_AVATARS), more: Math.max(0, ordered.length - STACKED_AVATARS) };
}

/** The group's members' rows, in row order. */
export function groupRows(group: TravelGroup, rows: readonly MemberRow[]): MemberRow[] {
  const ids = new Set(group.memberIds);
  return rows.filter((row) => ids.has(row.userId));
}

/** "Driving together · Alice, Bob, you": the motion word from the group's mode, the caller last. */
export function groupTitle(group: TravelGroup, rows: readonly MemberRow[]): string {
  const word = group.mode === "driving" ? "Driving" : group.mode === "walking" ? "Walking" : "On the move";
  const members = groupRows(group, rows);
  const names = members.filter((row) => !row.isSelf).map((row) => row.name);
  if (members.some((row) => row.isSelf)) names.push("you");
  return `${word} together · ${names.join(", ")}`;
}

/** A sidebar entry: a member alone, or a group with its members beneath it. */
export type SidebarEntry =
  { kind: "member"; row: MemberRow } | { kind: "group"; group: TravelGroup; key: string; rows: MemberRow[] };

/** Rows with each group's members gathered into one entry where its first member was. */
export function sidebarEntries(rows: readonly MemberRow[], groups: readonly TravelGroup[]): SidebarEntry[] {
  const groupOf = new Map<string, TravelGroup>();
  for (const group of groups) for (const id of group.memberIds) groupOf.set(id, group);
  const entries: SidebarEntry[] = [];
  const placed = new Set<string>();
  for (const row of rows) {
    const group = groupOf.get(row.userId);
    if (!group) entries.push({ kind: "member", row });
    else if (!placed.has(group.id)) {
      placed.add(group.id);
      entries.push({ kind: "group", group, key: groupKey(group.id), rows: groupRows(group, rows) });
    }
  }
  return entries;
}

/**
 * Members not drawn on their own: everyone in a group but its freshest member,
 * whose marker (and moving trail) stands for the group.
 */
export function hiddenMembers(groups: readonly TravelGroup[]): Set<string> {
  return new Set(groups.flatMap((group) => group.memberIds.filter((id) => id !== group.freshestId)));
}

/** The rows drawn as points (a group as its freshest member), for fitting the camera. */
export function drawnRows(rows: readonly MemberRow[], groups: readonly TravelGroup[]): MemberRow[] {
  const hidden = hiddenMembers(groups);
  return hidden.size === 0 ? [...rows] : rows.filter((row) => !hidden.has(row.userId));
}

/**
 * The moving trails to draw: every track in `tracks` (user id → points) but
 * one per group (its freshest member's), and none for `selectedId` (the
 * selected member, whose longer trail is drawn instead) or, if they are
 * grouped, for their group, so one trail stays drawn there.
 */
export function shownTracks(
  tracks: ReadonlyMap<string, readonly HistoryPoint[]>,
  groups: readonly TravelGroup[],
  selectedId: string | null,
): Map<string, readonly HistoryPoint[]> {
  const hidden = hiddenMembers(groups);
  const selectedGroup = groups.find((group) => selectedId !== null && group.memberIds.includes(selectedId));
  for (const id of selectedGroup?.memberIds ?? []) hidden.add(id);
  return new Map([...tracks].filter(([id]) => id !== selectedId && !hidden.has(id)));
}

/**
 * What the map should follow now that the groups changed from `before` to
 * `after`, given it followed `target` (a user id or {@link groupKey}). A member
 * stays selected; a group that changed its id (its smallest member got out)
 * is followed as the group now holding its freshest member, else any of its
 * members; one that dissolved, as its freshest member.
 */
export function retarget(target: string, before: readonly TravelGroup[], after: readonly TravelGroup[]): string {
  const id = groupIdOf(target);
  if (id === null || after.some((group) => group.id === id)) return target;
  const was = before.find((group) => group.id === id);
  if (!was) return target;
  const ids = [was.freshestId, ...was.memberIds.filter((member) => member !== was.freshestId)];
  for (const member of ids) {
    const now = after.find((group) => group.memberIds.includes(member));
    if (now) return groupKey(now.id);
  }
  return was.freshestId;
}

/** The same groups (ids, members, freshest member and mode, in order), so an unchanged result keeps its identity. */
export function sameGroups(a: readonly TravelGroup[], b: readonly TravelGroup[]): boolean {
  return (
    a.length === b.length &&
    a.every((x, i) => {
      const y = b[i]!;
      return (
        x.id === y.id &&
        x.freshestId === y.freshestId &&
        x.mode === y.mode &&
        x.memberIds.length === y.memberIds.length &&
        x.memberIds.every((id, k) => id === y.memberIds[k])
      );
    })
  );
}
